import { useEffect, useRef } from 'react';

const areas = [
  {
    icon: 'ri-building-line',
    title: 'Colegios',
    subtitle: 'Educación Temprana',
    description:
      'Sembramos consciencia ambiental desde la infancia en los colegios de los 87 municipios de Santander, creando generaciones comprometidas con el planeta a través de programas educativos interactivos y talleres de reciclaje.',
    color: '#43a047',
    photo: 'https://readdy.ai/api/search-image?query=Colombian%20school%20children%20outdoor%20recycling%20workshop%20in%20Bucaramanga%2C%20bright%20sunny%20day%2C%20green%20trees%2C%20students%20sorting%20plastic%20bottles%20and%20paper%20in%20colorful%20bins%2C%20warm%20natural%20light%2C%20cheerful%20atmosphere%2C%20realistic%20photography%2C%20Santander%20Colombia&width=600&height=700&seq=impact_school01&orientation=portrait',
  },
  {
    icon: 'ri-community-line',
    title: 'Parques y Comunidades',
    subtitle: 'Impacto Comunitario',
    description:
      'Transformamos los parques y espacios públicos de Santander en puntos de reciclaje activos, involucrando a vecinos y familias en la construcción de entornos más limpios y sostenibles desde 2022.',
    color: '#2e7d32',
    photo: 'https://readdy.ai/api/search-image?query=beautiful%20urban%20park%20in%20Bucaramanga%20Colombia%20with%20benches%20and%20walkways%20made%20of%20recycled%20plastic%20lumber%2C%20lush%20tropical%20trees%2C%20families%20walking%2C%20golden%20afternoon%20light%2C%20realistic%20photo%20quality%2C%20green%20sustainable%20infrastructure%2C%20Santander&width=600&height=700&seq=impact_park01&orientation=portrait',
  },
  {
    icon: 'ri-graduation-cap-line',
    title: 'Universidades',
    subtitle: 'Liderazgo Juvenil',
    description:
      'Formamos líderes ambientales en las universidades de Santander que replican el modelo de reciclaje consciente en sus comunidades, multiplicando el impacto de cada acción en los 87 municipios.',
    color: '#c62828',
    photo: 'https://readdy.ai/api/search-image?query=young%20university%20students%20in%20Colombia%20doing%20environmental%20activism%20on%20campus%2C%20holding%20recycled%20materials%2C%20green%20campus%20with%20trees%2C%20warm%20daylight%2C%20diverse%20group%20smiling%2C%20Bucaramanga%20Santander%2C%20realistic%20high%20quality%20photography&width=600&height=700&seq=impact_univ01&orientation=portrait',
  },
  {
    icon: 'ri-briefcase-line',
    title: 'Empresas',
    subtitle: 'Responsabilidad Corporativa',
    description:
      'Acompañamos a empresas santandereanas en la implementación de sistemas de reciclaje eficientes, reduciendo su huella ambiental y fortaleciendo su compromiso con el departamento desde 2022.',
    color: '#e65100',
    photo: 'https://readdy.ai/api/search-image?query=modern%20Colombian%20company%20facility%20with%20green%20recycling%20station%20inside%2C%20workers%20sorting%20waste%20materials%2C%20clean%20industrial%20interior%20with%20natural%20light%2C%20corporate%20social%20responsibility%2C%20Santander%20Colombia%2C%20realistic%20professional%20photography&width=600&height=700&seq=impact_corp01&orientation=portrait',
  },
];

export default function ImpactSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll('.impact-card');
            cards.forEach((card, i) => {
              setTimeout(() => {
                (card as HTMLElement).style.opacity = '1';
                (card as HTMLElement).style.transform = 'translateY(0)';
              }, i * 150);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-20 md:py-28 bg-white px-6 md:px-14" ref={sectionRef}>
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-14">
        <p
          className="text-[#9ca3af] text-xs tracking-widest uppercase mb-3"
          style={{ fontFamily: "'Montserrat', sans-serif" }}
        >
          Nuestro Alcance · 87 Municipios · Desde 2022
        </p>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <h2 style={{ fontFamily: "'Playfair Display', serif" }} className="text-4xl md:text-5xl font-bold leading-tight">
            <span className="text-[#1b5e20]">Transformación</span>
            <br />
            <span className="text-[#43a047]">que Impacta a Todos</span>
          </h2>
          <p className="text-[#6b7280] text-sm max-w-md leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            FRAIS no es solo una recicladora — es una transformadora de materiales. Desde colegios hasta empresas, llevamos la ingeniería circular a los 87 municipios de Santander desde 2022.
          </p>
        </div>
      </div>

      {/* Cards Grid — now with real photo backgrounds */}
      <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {areas.map((area) => (
          <div
            key={area.title}
            className="impact-card relative rounded-2xl overflow-hidden group cursor-default"
            style={{
              opacity: 0,
              transform: 'translateY(30px)',
              transition: 'opacity 0.6s ease, transform 0.6s ease',
              minHeight: '320px',
            }}
          >
            {/* Real photo background */}
            <img
              src={area.photo}
              alt={area.title}
              className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
            />
            {/* Gradient overlay — stronger for readability */}
            <div
              className="absolute inset-0 transition-opacity duration-300"
              style={{ background: `linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.55) 40%, rgba(0,0,0,0.25) 100%)` }}
            ></div>

            {/* Content */}
            <div className="relative z-10 flex flex-col justify-end h-full p-6" style={{ minHeight: '320px' }}>
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center mb-3 bg-white/20 backdrop-blur-sm"
              >
                <i className={`${area.icon} text-white text-lg`}></i>
              </div>
              <p className="text-white/65 text-[10px] tracking-widest uppercase mb-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                {area.subtitle}
              </p>
              <h3
                className="text-white font-bold text-lg mb-2 leading-tight"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {area.title}
              </h3>
              <p className="text-white/85 text-xs leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                {area.description}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
